import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { youtubeRssService } from '@/lib/youtubeRss';
import { useAuth } from '@/hooks/useAuth';
import { Video } from '@/lib/api';
import { toast } from 'sonner';
import { useRealtimeUpdates } from '@/hooks/useRealtimeUpdates';

interface VideosByDate {
  date: string;
  videos: Video[];
}

export const usePerformantFeed = () => {
  const [allVideos, setAllVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [weeksLoaded, setWeeksLoaded] = useState(1);
  const [newVideosCount, setNewVideosCount] = useState(0);
  const [usingCache, setUsingCache] = useState(false);
  const [usingRSS, setUsingRSS] = useState(false);
  const mountedRef = useRef(true);
  const { user } = useAuth();

  // Local cache key
  const cacheKey = 'youtube_feed_cache';
  const cacheTimestampKey = 'youtube_feed_cache_timestamp';

  // Function to get channel avatar using YouTube API
  const getChannelAvatar = async (channelId: string, channelName: string): Promise<string> => {
    if (!channelId) {
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(channelName || 'Creator')}&background=random&color=fff`;
    }

    try {
      // First check if we have it in Supabase creators table
      const { data: creatorData, error } = await supabase
        .from('creators')
        .select('avatar')
        .eq('creator_id', channelId)
        .single();

      if (!error && creatorData?.avatar) {
        return creatorData.avatar;
      }

      // If not in cache, try to fetch from YouTube API
      const apiKey = await supabase
        .from('profiles')
        .select('youtube_api_key')
        .eq('id', user?.id)
        .single();

      if (apiKey.data?.youtube_api_key) {
        const response = await fetch(
          `https://www.googleapis.com/youtube/v3/channels?part=snippet&id=${channelId}&key=${apiKey.data.youtube_api_key}`
        );

        if (response.ok) {
          const data = await response.json();
          if (data.items && data.items.length > 0) {
            const avatar = data.items[0].snippet.thumbnails.medium?.url || 
                          data.items[0].snippet.thumbnails.default?.url;
            
            if (avatar) {
              // Save to creators table for future use
              await supabase
                .from('creators')
                .upsert({
                  creator_id: channelId,
                  name: channelName,
                  avatar: avatar,
                  subscribers: 0,
                  is_subscribed: false
                });
              
              return avatar;
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching channel avatar:', error);
    }

    // Fallback to generated avatar
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(channelName || 'Creator')}&background=random&color=fff`;
  };

  const convertRSSToVideo = async (rssVideo: any): Promise<Video | null> => {
    if (!rssVideo || !rssVideo.id) return null;
    
    // Assurer que la date est correctement parsée
    let publishedDate: string;
    if (rssVideo.publishedDate) {
      // Si c'est déjà une string ISO, l'utiliser directement
      if (typeof rssVideo.publishedDate === 'string') {
        publishedDate = rssVideo.publishedDate;
      } else {
        // Si c'est un objet Date, le convertir en ISO string
        publishedDate = new Date(rssVideo.publishedDate).toISOString();
      }
    } else {
      // Fallback à la date actuelle si pas de date disponible
      publishedDate = new Date().toISOString();
    }
    
    const channelAvatar = rssVideo.channelAvatar || await getChannelAvatar(rssVideo.channelId, rssVideo.channelName);
    
    return {
      id: rssVideo.id,
      title: rssVideo.title || '',
      thumbnail: rssVideo.thumbnail || '',
      channelName: rssVideo.channelName || '',
      channelAvatar,
      channelId: rssVideo.channelId || '',
      published: rssVideo.published || '',
      publishedDate,
      duration: rssVideo.duration || '',
      views: 0
    };
  };

  const convertCachedToVideo = async (cachedVideo: any): Promise<Video | null> => {
    if (!cachedVideo || !cachedVideo.video_id) return null;
    
    // Assurer que la date est correctement parsée depuis le cache
    let publishedDate: string;
    if (cachedVideo.published_date) {
      // Si c'est déjà une string de date, créer un objet Date puis convertir en ISO
      publishedDate = new Date(cachedVideo.published_date).toISOString();
    } else {
      // Fallback à la date actuelle
      publishedDate = new Date().toISOString();
    }
    
    const channelAvatar = cachedVideo.channel_avatar || await getChannelAvatar(cachedVideo.channel_id, cachedVideo.channel_name);
    
    return {
      id: cachedVideo.video_id,
      title: cachedVideo.title || '',
      thumbnail: cachedVideo.thumbnail || '',
      channelName: cachedVideo.channel_name || '',
      channelAvatar,
      channelId: cachedVideo.channel_id || '',
      published: cachedVideo.published || '',
      publishedDate,
      duration: cachedVideo.duration || '',
      views: cachedVideo.views || 0
    };
  };

  // Phase 1: Load from local cache immediately
  const loadFromLocalCache = (): Video[] => {
    try {
      const cached = localStorage.getItem(cacheKey);
      const timestamp = localStorage.getItem(cacheTimestampKey);
      
      if (cached && timestamp) {
        const age = Date.now() - parseInt(timestamp);
        // Use cache if less than 1 hour old
        if (age < 60 * 60 * 1000) {
          return JSON.parse(cached);
        }
      }
    } catch (error) {
      console.error('Error loading local cache:', error);
    }
    return [];
  };

  // Save to local cache
  const saveToLocalCache = (videos: Video[]) => {
    try {
      localStorage.setItem(cacheKey, JSON.stringify(videos));
      localStorage.setItem(cacheTimestampKey, Date.now().toString());
    } catch (error) {
      console.error('Error saving to local cache:', error);
    }
  };

  const getUserSubscriptions = async (): Promise<string[]> => {
    if (!user) return [];
    
    const { data, error } = await supabase
      .from('profiles')
      .select('subscriptions')
      .eq('id', user.id)
      .single();
    
    if (error || !data?.subscriptions) return [];
    return data.subscriptions;
  };

  const getCachedVideos = async (subscriptionIds: string[]): Promise<Video[]> => {
    if (subscriptionIds.length === 0) return [];
    
    const { data, error } = await supabase
      .from('cached_videos_feed')
      .select('*')
      .in('channel_id', subscriptionIds)
      .order('published_date', { ascending: false })
      .limit(100);
    
    if (error) {
      console.error('Error fetching cached videos:', error);
      return [];
    }
    
    const results = await Promise.all(data?.map(convertCachedToVideo) || []);
    return results.filter(Boolean) as Video[];
  };

  const saveVideosToCache = async (videos: Video[]) => {
    if (videos.length === 0) return;
    
    const videosToSave = videos.map(video => ({
      video_id: video.id,
      title: video.title,
      thumbnail: video.thumbnail,
      channel_name: video.channelName,
      channel_avatar: video.channelAvatar,
      channel_id: video.channelId,
      published: video.published,
      published_date: new Date(video.publishedDate).toISOString().split('T')[0],
      duration: video.duration,
      views: video.views || 0
    }));
    
    const { error } = await supabase
      .from('cached_videos_feed')
      .upsert(videosToSave, { 
        onConflict: 'video_id',
        ignoreDuplicates: false 
      });
    
    if (error) {
      console.error('Error saving videos to cache:', error);
    }
  };

  // Phase 2: Incremental RSS fetch
  const getIncrementalVideos = async (subscriptionIds: string[], lastVideoDate?: Date): Promise<Video[]> => {
    if (subscriptionIds.length === 0) return [];
    
    try {
      const rssVideos = await youtubeRssService.getMultipleChannelsRSSVideos(subscriptionIds);
      const convertedVideos = await Promise.all(rssVideos.map(convertRSSToVideo));
      let newVideos = convertedVideos.filter(Boolean) as Video[];
      
      // Filter only videos newer than last cached video
      if (lastVideoDate) {
        newVideos = newVideos.filter(video => 
          new Date(video.publishedDate) > lastVideoDate
        );
      }
      
      return newVideos.sort((a, b) => 
        new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime()
      );
    } catch (error) {
      console.error('RSS fetch failed:', error);
      return [];
    }
  };

  // Main loading function implementing the hybrid strategy
  const loadVideos = async (isLoadMore = false) => {
    if (!user) return;
    
    if (!isLoadMore) setLoading(true);
    else setLoadingMore(true);
    
    setError(null);
    
    try {
      const subscriptionIds = await getUserSubscriptions();
      
      if (subscriptionIds.length === 0) {
        setAllVideos([]);
        return;
      }
      
      // Phase 1: Immediate cache loading
      if (!isLoadMore) {
        const localCached = loadFromLocalCache();
        if (localCached.length > 0) {
          setAllVideos(localCached);
          setUsingCache(true);
          setLoading(false);
        }
        
        // Also load from Supabase cache
        const supabaseCached = await getCachedVideos(subscriptionIds);
        if (supabaseCached.length > 0 && localCached.length === 0) {
          setAllVideos(supabaseCached);
          setUsingCache(true);
          setLoading(false);
          saveToLocalCache(supabaseCached);
        }
      }
      
      // Phase 2: Incremental RSS update
      const currentVideos = isLoadMore ? allVideos : (allVideos.length > 0 ? allVideos : await getCachedVideos(subscriptionIds));
      const lastVideoDate = currentVideos.length > 0 ? new Date(currentVideos[0].publishedDate) : undefined;
      
      const newVideos = await getIncrementalVideos(subscriptionIds, lastVideoDate);
      
      if (newVideos.length > 0) {
        setUsingRSS(true);
        setNewVideosCount(newVideos.length);
        
        // Merge and deduplicate
        const combinedVideos = [...newVideos, ...currentVideos];
        const uniqueVideos = Array.from(
          new Map(combinedVideos.map(v => [v.id, v])).values()
        ).sort((a, b) => new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime());
        
        setAllVideos(uniqueVideos);
        saveToLocalCache(uniqueVideos);
        await saveVideosToCache(newVideos);
        
        if (newVideos.length > 0) {
          toast.success(`${newVideos.length} nouvelles vidéos ajoutées`);
        }
      } else if (currentVideos.length === 0) {
        // Fallback to full cache if no videos at all
        const fallbackVideos = await getCachedVideos(subscriptionIds);
        setAllVideos(fallbackVideos);
        setUsingCache(true);
      }
      
    } catch (error) {
      console.error('Error loading videos:', error);
      setError('Erreur lors du chargement des vidéos');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Filter videos by weeks loaded
  const filterVideosByWeeks = (videos: Video[], weeks: number): Video[] => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - (weeks * 7));
    
    return videos.filter(video => 
      new Date(video.publishedDate) >= cutoffDate
    );
  };

  // Group videos by date (filtered by weeks) - utiliser la vraie date de publication
  const filteredVideos = filterVideosByWeeks(allVideos, weeksLoaded);
  const videosByDate = filteredVideos.reduce<VideosByDate[]>((acc, video) => {
    // Utiliser la date de publication réelle pour le groupement
    const videoDate = new Date(video.publishedDate);
    const date = videoDate.toISOString().split('T')[0];
    const existingGroup = acc.find(group => group.date === date);
    
    if (existingGroup) {
      existingGroup.videos.push(video);
    } else {
      acc.push({ date, videos: [video] });
    }
    
    return acc;
  }, []).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const formatDateHeader = (dateStr: string): string => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return "Aujourd'hui";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Hier";
    } else {
      return date.toLocaleDateString('fr-FR', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setUsingCache(false);
    setUsingRSS(false);
    setNewVideosCount(0);
    
    // Clear local cache to force fresh fetch
    localStorage.removeItem(cacheKey);
    localStorage.removeItem(cacheTimestampKey);
    
    await loadVideos();
    setRefreshing(false);
  };

  const loadMoreWeeks = async () => {
    setWeeksLoaded(prev => prev + 1);
  };

  // Intégration des mises à jour en temps réel
  const { triggerManualUpdate, lastUpdate } = useRealtimeUpdates({
    onVideoUpdate: () => {
      // Recharger les vidéos quand il y a des changements
      loadVideos();
    },
    onCreatorUpdate: () => {
      // Les créateurs seront automatiquement mis à jour grâce au cache
      console.log('Créateurs mis à jour automatiquement');
    },
    enablePolling: true,
    pollingInterval: 30 // 30 minutes
  });

  useEffect(() => {
    if (user && mountedRef.current) {
      loadVideos();
    }
    
    return () => {
      mountedRef.current = false;
    };
  }, [user]);

  const hasMoreWeeks = weeksLoaded < 8 && filteredVideos.length < allVideos.length;

  return {
    videosByDate,
    loading,
    loadingMore,
    refreshing,
    error,
    formatDateHeader,
    handleRefresh,
    loadMoreWeeks,
    hasMoreWeeks,
    newVideosCount,
    usingCache,
    usingRSS,
    triggerManualUpdate,
    lastUpdate
  };
};